import { Styled } from "./styles";
import { withErrorBoundary } from "@/components/hoc";
import { useLinkGoogle, useSubmit, useUnlinkGoogle } from "./hooks";
import { useUser } from "@/context/user";
import constant from "@/utils/constant";
import { yupResolver } from "@hookform/resolvers/yup";
import schemas from "@/utils/schemas";
import { useForm } from "react-hook-form";
import { Forms } from "@/types/form";
import { Box, Grid, Typography } from "@mui/material";
import { Input, Backdrop } from "@dippi/ui";
import React from "react";
import GoogleButton from "@/components/common/google-button";
import { Google } from "@dippi/icons";
import { useFormListeners } from "@dippi/forms";

const inputProps = {
    autoComplete: "new-password",
    form: {
        autoComplete: "off",
    },
};

const Profile: React.FC = () => {
    const SPACING = constant.GRID_SPACING;
    const { email, providerData } = useUser();

    const providers = providerData.map(i => i.providerId);
    const hasOnlyGoogleProvider = providers.length === 1 && providers[0] === "google.com";
    const hasOnlyEmailProvider = providers.length === 1 && providers[0] === "password";

    const { control, formState, handleSubmit } = useForm<Forms["Profile"]>({
        resolver: yupResolver(schemas.profile),
        values: {
            currentPassword: "",
            newPassword: "",
            repeatNewPassword: "",
            password: "",
            repeatPassword: "",
            email: email ?? "",
        },
    });
    const { isDirty, isSubmitting } = formState;
    useFormListeners("form-profile", isDirty, isSubmitting);

    const onSubmit = useSubmit();

    const { unLinking, onUnlink } = useUnlinkGoogle();
    const { linking, onLink } = useLinkGoogle();

    return (
        <Styled.Container>
            <Styled.Form
                id="form-profile"
                onSubmit={handleSubmit(async data => {
                    await onSubmit(data);
                })}
            >
                <Styled.Inputs
                    container
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={10}
                    xl={12}
                    padding={2}
                    gap={SPACING}
                >
                    <Grid item xs={12}>
                        <Typography variant="h3">Profile information</Typography>
                    </Grid>
                    <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                        <Input
                            testId="profile-email-input"
                            name="email"
                            control={control}
                            disabled={true}
                            type="email"
                        />
                    </Grid>

                    <Grid item container xs={12} gap={SPACING}>
                        {hasOnlyGoogleProvider ? (
                            <React.Fragment>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center">
                                        <Google style={{ marginRight: "10px" }} />
                                        <Typography>
                                            You've used Google provider to sign in. You can create a
                                            new password to use a password sign-in in combination
                                            with google sign-in.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                                    <Input
                                        testId="profile-password-input"
                                        name="password"
                                        control={control}
                                        type="password"
                                        className="sentry-mask"
                                        label="Password"
                                    />
                                </Grid>
                                <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                                    <Input
                                        testId="profile-repeat-password-input"
                                        name="repeatPassword"
                                        control={control}
                                        type="password"
                                        className="sentry-mask"
                                        label="Repeat password"
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                                    <Input
                                        testId="profile-current-password-input"
                                        name="currentPassword"
                                        control={control}
                                        inputProps={inputProps}
                                        type="password"
                                        className="sentry-mask"
                                        label="Current password"
                                    />
                                </Grid>
                                <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                                    <Input
                                        testId="profile-new-password-input"
                                        name="newPassword"
                                        control={control}
                                        inputProps={inputProps}
                                        type="password"
                                        className="sentry-mask"
                                        label="New password"
                                    />
                                </Grid>
                                <Grid item xl={3} lg={4} md={5} sm={8} xs={12}>
                                    <Input
                                        testId="profile-repeat-password-input"
                                        name="repeatNewPassword"
                                        control={control}
                                        inputProps={inputProps}
                                        type="password"
                                        className="sentry-mask"
                                        label="Repeat password"
                                    />
                                </Grid>
                                {hasOnlyGoogleProvider ? null : (
                                    <Styled.Google item xs={12}>
                                        <GoogleButton
                                            data-testid="profile-google-button"
                                            text={
                                                hasOnlyEmailProvider
                                                    ? "Link google provider"
                                                    : "Unlink google provider"
                                            }
                                            onClick={hasOnlyEmailProvider ? onLink : onUnlink}
                                        />
                                    </Styled.Google>
                                )}
                            </React.Fragment>
                        )}
                    </Grid>
                </Styled.Inputs>
                <Backdrop loading={isSubmitting || unLinking || linking} />
            </Styled.Form>
        </Styled.Container>
    );
};

export default withErrorBoundary({ component: "Profile" })(Profile);
